// SigmaStudioMatrixCharts.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import {
  VictoryChart,
  VictoryAxis,
  VictoryBar,
  VictoryLabel,
  VictoryTooltip,
} from "victory";
import './SigmaStudio.css';

const MATRIX_TYPES = {
  "C&E": {
    name: "Cause & Effect Matrix (L-shaped)",
    desc: "Prioritize inputs (Xs/causes) vs outputs (Ys/effects)",
    shape: "L",
    groups: 2,
    note: "Fully supported"
  },
  "Prioritization": {
    name: "Prioritization Matrix (Weighted L-shaped)",
    desc: "Rank solutions/ideas by weighted criteria",
    shape: "L (weighted)",
    groups: 2,
    note: "Fully supported with weights"
  },
  "T-shaped": {
    name: "T-shaped Matrix",
    desc: "One central group vs two independent groups",
    shape: "T",
    groups: 3,
    note: "Visualization coming soon"
  },
  "Y-shaped": {
    name: "Y-shaped Matrix",
    desc: "Interdependent relationships among three groups",
    shape: "Y",
    groups: 3,
    note: "Visualization coming soon"
  },
  "X-shaped": {
    name: "X-shaped Matrix",
    desc: "Interconnections among four groups",
    shape: "X",
    groups: 4,
    note: "Visualization coming soon"
  },
  "C-shaped": {
    name: "C-shaped Matrix (3D)",
    desc: "Three-way comparison of three groups",
    shape: "C",
    groups: 3,
    note: "Visualization coming soon"
  },
  "Roof": {
    name: "Roof-shaped Matrix (Correlation Roof)",
    desc: "Inter-relationships within one group",
    shape: "Roof",
    groups: 1,
    note: "Visualization coming soon (as overlay)"
  }
};

const SigmaStudioMatrixCharts = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedType, setSelectedType] = useState("C&E");

  const [title, setTitle] = useState("");
  const [remarks, setRemarks] = useState("");
  const [listA, setListA] = useState("");       // Rows: Inputs / Options / Ideas
  const [listB, setListB] = useState("");       // Columns: Outputs / Criteria
  const [listC, setListC] = useState("");       // For future T/Y/C types
  const [matrixDataText, setMatrixDataText] = useState("");
  const [weightsText, setWeightsText] = useState("");

  const [showHelp, setShowHelp] = useState(false);
  const [matrixResult, setMatrixResult] = useState(null);
  const [error, setError] = useState("");

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const populateExample = () => {
    setError("");
    setMatrixResult(null);

    if (selectedType === "C&E") {
      setTitle("Label Printing Defects Prioritization");
      setRemarks("Team scoring: strength of influence on each defect type");
      setListA("Ink viscosity\nPrint speed\nPaper tension\nPlate alignment\nDrying temp\nRoller pressure");
      setListB("Misregistration\nColor variation\nSmearing\nGhosting\nPinholes\nBleeding");
      setMatrixDataText("9,3,1,0,1,3\n1,9,3,1,0,3\n3,1,9,3,1,0\n9,0,3,9,1,1\n1,3,0,1,9,3\n3,1,3,0,1,9");
      setWeightsText("");
      setListC("");
    } else if (selectedType === "Prioritization") {
      setTitle("Kaizen Idea Quick-Win Selection");
      setRemarks("Ideas scored against weighted business criteria");
      setListA("Auto ink feed system\nUpgrade dryer unit\nAdd web tension sensor\nOperator training module");
      setListB("Cost savings\nThroughput increase\nDefect reduction\nImplementation ease");
      setMatrixDataText("5,8,3,9\n2,9,7,4\n7,4,8,6\n3,2,5,8");
      setWeightsText("4, 7, 9, 5");
      setListC("");
    } else {
      setTitle(`${MATRIX_TYPES[selectedType]?.name || "Matrix"} Example`);
      setRemarks("Enter your groups below...");
      setListA("Item A1\nItem A2\nItem A3");
      setListB("Item B1\nItem B2\nItem B3");
      setListC("Item C1\nItem C2");
      setMatrixDataText("");
      setWeightsText("");
    }
  };

  const parseList = (str) => str.split('\n').map(l => l.trim()).filter(Boolean);

  const parseNumbers = (str) => {
    return str.split(/[\s,;\n]+/)
      .map(v => v.trim())
      .filter(v => v !== "" && !isNaN(Number(v)))
      .map(Number);
  };

  // const parseMatrixScores = (str, rows, cols) => {
  //   if (!str.trim()) return [];
  //   const lines = parseList(str);
  //   if (lines.length !== rows) return null;
  //   const data = [];
  //   lines.forEach((line, rowIdx) => {
  //     const vals = line.split(/[\s,]+/).map(v => parseFloat(v)).filter(v => !isNaN(v));
  //     if (vals.length !== cols) return null;
  //     vals.forEach((val, colIdx) => {
  //       data.push({
  //         x: colIdx + 1,
  //         y: rows - rowIdx,           // invert y so first row is at top
  //         value: val,
  //         label: val > 0 ? val.toString() : ""
  //       });
  //     });
  //   });
  //   return data;
  // };

  const parseMatrixScores = (str, rows, cols) => {
    if (!str.trim()) return [];
    const lines = parseList(str);
    if (lines.length !== rows) return null;
    const data = [];
    lines.forEach((line, rowIdx) => {
      const vals = line.split(/[\s,]+/).map(v => parseFloat(v)).filter(v => !isNaN(v));
      if (vals.length !== cols) return null;
      vals.forEach((val, colIdx) => {
        data.push({
          x: colIdx + 1,
          y: rows - rowIdx,
          value: val,
          label: val > 0 ? val.toString() : ""
        });
      });
    });
    return data;
  };
  
  const calculateMatrix = () => {
    setError("");
    setMatrixResult(null);

    if (!title.trim()) return setError("Title is required.");

    const itemsA = parseList(listA);
    const itemsB = parseList(listB);
    const itemsC = parseList(listC);

    if (itemsA.length === 0) return setError("At least one item in List A required.");

    const result = {
      title,
      remarks,
      type: selectedType,
      itemsA,
      itemsB,
      itemsC,
      heatmapData: [],
      ranked: [],
      totals: [],
      weights: []
    };

    if (selectedType === "C&E" || selectedType === "Prioritization") {
      if (itemsB.length === 0) return setError("At least one item in List B required.");

      const rawScores = parseMatrixScores(matrixDataText, itemsA.length, itemsB.length);
      if (matrixDataText.trim() && rawScores === null) {
        return setError("Score format invalid. Each row must have exactly " + itemsB.length + " numbers (0/1/3/9).");
      }

      const scores = rawScores || Array(itemsA.length * itemsB.length).fill({ value: 0 });

      let weights = selectedType === "Prioritization" && weightsText.trim()
        ? parseNumbers(weightsText)
        : Array(itemsB.length).fill(1);

      if (weights.length !== itemsB.length) {
        return setError(`Weights count (${weights.length}) must match criteria count (${itemsB.length}).`);
      }

      let totalsPerColumn = Array(itemsB.length).fill(0);
      const strengthMap = {};

      scores.forEach(d => {
        const col = d.x - 1;
        const row = itemsA.length - d.y;
        const weighted = d.value * weights[col];
        totalsPerColumn[col] += weighted;
        strengthMap[`${row}-${col}`] = d.value;
      });

      const rankedRows = itemsA.map((item, rowIdx) => {
        let sum = 0;
        for (let col = 0; col < itemsB.length; col++) {
          sum += (strengthMap[`${rowIdx}-${col}`] || 0) * weights[col];
        }
        return { item, total: sum };
      }).sort((a, b) => b.total - a.total);

      result.heatmapData = scores;
      result.ranked = rankedRows;
      result.totals = totalsPerColumn;
      result.weights = weights;
    } else {
      result.message = "Data captured — visualization for this matrix type is planned for a future update.";
    }

    setMatrixResult(result);
  };

  const getColor = (v) => {
    if (v >= 9) return "#f56565";
    if (v >= 3) return "#48bb78";
    if (v >= 1) return "#3182ce";
    return "#1a202c";
  };

  const renderLMatrix = (data, rowCount, colCount, rowLabels, colLabels) => {
    const chartHeight = Math.max(400, rowCount * 45 + 140);

    return (
      <div style={{ height: `${chartHeight}px`, width: "100%", overflow: "auto" }}>
        <VictoryChart
          height={chartHeight}
          padding={{ top: 100, bottom: 160, left: 180, right: 60 }}
          domainPadding={{ x: 20, y: 20 }}
        >
          <VictoryAxis
            tickValues={colLabels.map((_, i) => i + 1)}
            tickFormat={colLabels}
            style={{
              axis: { stroke: "#718096" },
              tickLabels: { fill: "#e2e8f0", fontSize: 11, angle: -45, textAnchor: "end" },
              axisLabel: { fill: "#a0aec0", fontSize: 14, padding: 110 },
            }}
            label="Outputs / Criteria"
          />

          <VictoryAxis
            dependentAxis
            tickValues={rowLabels.map((_, i) => i + 1)}
            tickFormat={rowLabels}
            style={{
              axis: { stroke: "#718096" },
              tickLabels: { fill: "#e2e8f0", fontSize: 11, textAnchor: "end" },
              axisLabel: { fill: "#a0aec0", fontSize: 14, angle: -90, padding: 160 },
            }}
            label="Inputs / Options"
          />

          {data.map((d, idx) => (
            <VictoryBar
              key={idx}
              data={[{ x: d.x, y: d.y, value: d.value, label: d.label }]}
              barWidth={32}
              barRatio={0.9}
              style={{
                data: { fill: getColor(d.value), stroke: "#2d3748", strokeWidth: 1 }
              }}
              labels={({ datum }) => datum.label}
              labelComponent={
                <VictoryTooltip
                  flyoutStyle={{ fill: "#1a202c", stroke: "#4a5568" }}
                  style={{ fill: "#e2e8f0", fontSize: 11 }}
                />
              }
            />
          ))}

          <VictoryLabel text="9 = Strong" x={60} y={50} style={{ fill: "#f56565", fontSize: 11 }} />
          <VictoryLabel text="3 = Medium" x={60} y={70} style={{ fill: "#48bb78", fontSize: 11 }} />
          <VictoryLabel text="1 = Weak" x={60} y={90} style={{ fill: "#3182ce", fontSize: 11 }} />
        </VictoryChart>
      </div>
    );
  };

  const renderHelp = () => (
    <div className="mt-6 p-6 bg-gray-900 rounded-xl border border-teal-900/50">
      <h2 className="text-xl lg:text-2xl font-bold text-yellow-300 mb-6 text-center">
        Help – Matrix Charts for MSMEs
      </h2>
      {/* ... keep your existing help content ... */}
      <div className="text-center mt-8">
        <button onClick={() => setShowHelp(false)} className="w-40 px-2 py-2 bg-teal-700 hover:bg-teal-600 rounded-lg font-medium">
          Close Help
        </button>
      </div>
    </div>
  );

  const isLType = selectedType === "C&E" || selectedType === "Prioritization";

  return (
    <div className="min-h-screen flex flex-col bg-black [&_p]:text-white [&_li]:text-gray-100 text-gray-100 font-sans">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">
            📊 Think5Whys Sigma Studio
          </div>
          <div className="text-[11px] text-gray-200">Matrix Charts – Prioritization & Relationship Tools for MSMEs</div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="px-3 py-1 rounded-md bg-red-600 hover:bg-red-700 text-sm">
                ⏻ Logout
              </button>
            </>
          ) : (
            <button onClick={() => navigate("/login")} className="px-3 py-1 rounded border border-gray-600 text-sm">
              🔐 Login
            </button>
          )}
        </div>

        <button className="md:hidden text-2xl text-teal-300" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-700 px-4 py-4">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-4">
              <div className="text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="px-6 py-2 bg-red-700 rounded">Logout</button>
            </div>
          ) : (
            <div className="text-center">
              <button onClick={() => navigate("/login")} className="px-6 py-2 border rounded">Login</button>
            </div>
          )}
        </div>
      )}

      <main className="flex-1 p-4 pb-12 max-w-6xl mx-auto">
        <div className="grid grid-cols-3 gap-2 place-items-center mb-4">
          <button onClick={populateExample} className="text-sm px-3 py-2 bg-green-700 hover:bg-green-600 rounded-md font-medium">
            Populate Example ({selectedType})
          </button>
          <button onClick={() => setShowHelp(!showHelp)} className="text-sm px-3 py-2 bg-blue-700 hover:bg-blue-600 rounded-md font-medium">
            {showHelp ? "Hide" : "Show"} Help
          </button>
        </div>

        {showHelp && renderHelp()}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block mb-2 text-cyan-300 font-medium">Matrix Type</label>
            <select
              className="w-full h-12 p-3 bg-gray-900 border border-gray-700 rounded text-white"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              {Object.entries(MATRIX_TYPES).map(([key, { name, desc }]) => (
                <option key={key} value={key}>{name} — {desc}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-2 text-cyan-300 font-medium">Title</label>
            <input
              type="text"
              className="w-full p-3 bg-gray-900 border border-gray-700 rounded text-white"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`e.g. ${MATRIX_TYPES[selectedType]?.name || "Matrix"}`}
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block mb-2 text-cyan-300 font-medium">Remarks / Notes</label>
          <textarea
            className="w-full h-24 p-3 bg-gray-900 border border-gray-700 rounded text-white"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Team, date, scope, special notes..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block mb-2 text-cyan-300 font-medium">
              {isLType ? "Inputs / Causes / Options (rows)" : "Group A"}
            </label>
            <textarea
              className="w-full h-40 p-3 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm"
              value={listA}
              onChange={(e) => setListA(e.target.value)}
              placeholder="One per line..."
            />
          </div>

          <div>
            <label className="block mb-2 text-cyan-300 font-medium">
              {isLType ? "Outputs / Criteria (columns)" : "Group B"}
            </label>
            <textarea
              className="w-full h-40 p-3 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm"
              value={listB}
              onChange={(e) => setListB(e.target.value)}
              placeholder="One per line..."
            />
          </div>

          {selectedType.startsWith("T") || selectedType.startsWith("Y") || selectedType === "C-shaped" ? (
            <div>
              <label className="block mb-2 text-cyan-300 font-medium">Group C</label>
              <textarea
                className="w-full h-40 p-3 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm"
                value={listC}
                onChange={(e) => setListC(e.target.value)}
                placeholder="One per line..."
              />
            </div>
          ) : null}
        </div>

        {isLType && (
          <>
            <div className="mb-6">
              <label className="block mb-2 text-cyan-300 font-medium">
                Relationship Scores (optional – one row per input, space/comma separated: 0,1,3,9)
              </label>
              <textarea
                className="w-full h-32 p-3 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm"
                value={matrixDataText}
                onChange={(e) => setMatrixDataText(e.target.value)}
                placeholder={`9 3 1 0\n1 9 3 1\n... (${listB.split('\n').length || "n"} values per line)`}
              />
            </div>

            {selectedType === "Prioritization" && (
              <div className="mb-6">
                <label className="block mb-2 text-cyan-300 font-medium">Criteria Weights (space/comma separated)</label>
                <input
                  type="text"
                  className="w-full p-3 bg-gray-900 border border-gray-700 rounded text-white"
                  value={weightsText}
                  onChange={(e) => setWeightsText(e.target.value)}
                  placeholder="4, 7, 9, 5"
                />
              </div>
            )}
          </>
        )}

        <div className="flex justify-center mb-8">
          <button
            onClick={calculateMatrix}
            className="w-48 py-2 bg-purple-700 hover:bg-purple-600 rounded-lg font-medium disabled:opacity-50"
            disabled={!title.trim() || !listA.trim() || (isLType && !listB.trim())}
          >
            Generate Matrix
          </button>
        </div>

        {error && (
          <div className="p-4 mb-6 bg-red-900/70 border border-red-700 rounded text-red-100 text-center">
            {error}
          </div>
        )}

        {matrixResult && (
          <div className="mt-8 p-6 bg-gray-950 border border-gray-700 rounded-xl">
            <h2 className="text-2xl text-teal-300 mb-4 text-center">{matrixResult.title}</h2>
            {matrixResult.remarks && <p className="text-gray-300 mb-6 text-center italic">{matrixResult.remarks}</p>}

            {isLType ? (
              <>
                {matrixResult.heatmapData.length > 0 ? (
                  renderLMatrix(
                    matrixResult.heatmapData,
                    matrixResult.itemsA.length,
                    matrixResult.itemsB.length,
                    matrixResult.itemsA,
                    matrixResult.itemsB
                  )
                ) : (
                  <p className="text-center text-gray-400">No scores entered → empty matrix shown. Enter scores to see colors.</p>
                )}

                <div className="mt-10 p-5 bg-gray-900 rounded-lg border border-gray-700">
                  <h3 className="text-xl text-teal-300 mb-4">
                    Prioritized {selectedType === "Prioritization" ? "Options/Ideas" : "Inputs"} (by total weighted score)
                  </h3>
                  <ol className="list-decimal pl-6 space-y-2 text-gray-200">
                    {matrixResult.ranked.map((r, i) => (
                      <li key={i}>
                        {r.item} — <span className="font-bold">{r.total.toFixed(1)}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </>
            ) : (
              <div className="p-8 bg-gray-800 rounded-xl text-center text-gray-300">
                <h3 className="text-2xl text-yellow-300 mb-4">{MATRIX_TYPES[selectedType]?.name}</h3>
                <p className="mb-4">{matrixResult.message}</p>
                <p className="text-sm text-gray-500">Visualization support planned for future release.</p>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-auto">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <div className="text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions
          </div>
          <div className="flex flex-wrap gap-6">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SigmaStudioMatrixCharts;